import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import {
  clearBackendToken,
  exchangeLogtoForBackend,
  getApiAccessToken,
} from '@/lib/logto';

/**
 * Main RTK Query API for the QLASH Laravel backend.
 *
 * Auth: Laravel's `auth:api` guard (Passport) is used on write endpoints
 * (POST /tournaments/:id/participants, etc.) and only accepts Passport-
 * issued bearer tokens — NOT raw Logto JWTs. The mobile-app and this
 * Mini App both follow the same pattern: exchange the Logto access token
 * for a Passport token via POST /auth/exchange-token
 * (MachineTokenController::exchange → LogtoTokenVerifier → Passport),
 * cache that, send it on every request. On 401 we wipe the cache and
 * re-exchange once.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.apiUrl,
  prepareHeaders: async (headers, { endpoint }) => {
    const token = await getApiAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);

    if (endpoint === 'updateAvatar' || endpoint === 'tournamentMatchesResultImage') {
      headers.set('Content-Type', 'multipart/form-data');
    } else {
      headers.set('Content-Type', 'application/json');
    }
    headers.set('Accept', 'application/json');
    headers.set('platform', 'telegram');
    headers.set('x-platform-id', env.platformId);
    return headers;
  },
});

const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  apiCtx,
  extraOptions,
) => {
  let result = await rawBaseQuery(args, apiCtx, extraOptions);
  if (result.error?.status === 401) {
    clearBackendToken();
    const fresh = await exchangeLogtoForBackend();
    if (fresh) result = await rawBaseQuery(args, apiCtx, extraOptions);
  }
  return result;
};

export const api = createApi({
  reducerPath: 'api',
  baseQuery,
  tagTypes: ['Users', 'Profiles', 'Teams', 'Tournaments', 'Matches', 'Chats', 'Leaderboard', 'Games'],
  endpoints: () => ({}),
});

/**
 * Endpoint paths copied from mobile-app/src/services/api.ts.
 * Login / Register / PasswordRecovery removed: auth is handled by Logto OIDC.
 */
export enum Api {
  Logout = '/logout',
  Users = '/users',
  UsersDelete = '/users/me/delete',
  UpdateUsers = '/users/:id',
  UpdateAvatar = '/users/:id/image',
  WsToken = '/users/:id/ws-token',
  FavoriteGames = '/users/:id/favourite-games',
  Profiles = '/users/:id/profiles',
  ProfilesId = '/users/:id/profiles/:profileId',
  ProfilesIdRiot = 'users/:id/profiles/:profileId/riot',
  UserTournaments = '/users/:id/tournaments',
  CreateProfile = '/users/me/profiles',
  Terms = '/terms/:countryId/latest',
  MyTournaments = '/my-tournaments',
  Tournaments = '/tournaments',
  TournamentsArena = '/tournaments/:id/arena',
  TournamentId = '/tournaments/:id',
  TournamentRules = '/rules/:id',
  TournamentParticipants = '/tournaments/:id/participants',
  TournamentMatches = '/tournaments/:id/matches',
  TournamentMatcheDetail = '/tournaments/:id/matches/:matchId',
  TournamentMatchesResult = '/tournaments/:id/matches/:matchId/result',
  RemoveParticipant = '/tournaments/:id/participants/:participant_id',
  Leaderboard = '/leaderboard',
  Seasons = '/seasons',
  Leaderboards = '/leaderboards',
  Games = '/games/available',
  Platforms = '/platforms',
  Countries = '/countries',
  ListChats = '/list-chats',
  Chat = '/chat/:id',
  Notification = '/notifications',
  Advertisements = '/advertisements',
  MatchResult = '/tournaments/:tournament/matches/:match/result',
  MatchCheckin = '/tournaments/:id/matches/:matchId/checkin',
  MatchForfeit = '/tournaments/:id/matches/:matchId/forfeit',
}
